import classNames from 'classnames';
import { flatten } from 'lodash-es';

import { useMilestoneStepperStyles } from './MilestoneStepper.styles';

export const StepType = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
};

/** returns dataSource enriched by position (%) [{ item, position }] */
export function computePositions(dataSource) {
  const structured = [];
  let lastPrimaryStep;
  for (const item of dataSource) {
    if (item.type === StepType.SECONDARY) {
      lastPrimaryStep.subSteps.push(item);
    } else {
      lastPrimaryStep = { item, subSteps: [] };
      structured.push(lastPrimaryStep);
    }
  }

  const steps = structured.map(({ subSteps, item }, i) => {
    const parentStep = 100 / (structured.length - 1);
    const parentPosition = i * parentStep;
    return [
      {
        item,
        position: parentPosition,
      },
      ...subSteps.map((child, j) => {
        const childStep = parentStep / (subSteps.length + 1);
        const childPosition = (j + 1) * childStep;
        return {
          item: child,
          position: parentPosition + childPosition,
        };
      }),
    ];
  });
  return flatten(steps);
}

function CircleStep({ icon, active, isLeading, isLeadingStepActivated }) {
  const activated = !isLeading || isLeadingStepActivated;
  const { styles } = useMilestoneStepperStyles();
  return (
    <div
      className={classNames(styles.circleStep, {
        active: activated && active,
      })}
    >
      <div className={styles.circleStepOuter} />
      <div className={styles.circleStepInner}>{icon}</div>
    </div>
  );
}

function RectStep({ icon, active, isLeading, isLeadingStepActivated }) {
  const activated = !isLeading || isLeadingStepActivated;
  const { styles } = useMilestoneStepperStyles();
  return (
    <div
      className={classNames(styles.rectStep, {
        active: activated && active,
        deactivated: !activated,
      })}
    >
      <div className={styles.rectStepInner} />
    </div>
  );
}

function MilestoneTimeline({ steps, activeStep, isLeadingStepActivated }) {
  const activeIdx = steps.findIndex(({ item }) => item.key === activeStep);
  const activeItem = steps.find(({ item }) => item.key === activeStep);
  const { styles } = useMilestoneStepperStyles();
  return (
    <div className={styles.timeline}>
      {activeStep && activeItem && (
        <div
          className={styles.progressBar}
          style={{ right: `${100 - activeItem.position}%` }}
        />
      )}
      {steps.map(({ position, item }, idx) => {
        const Step = item.type === StepType.SECONDARY ? RectStep : CircleStep;
        return (
          <div
            className={styles.step}
            style={{ left: `${position}%` }}
            key={item.key}
          >
            <Step
              icon={item.icon}
              active={activeStep && activeIdx >= idx}
              isLeading={activeStep && idx === activeIdx}
              isLeadingStepActivated={isLeadingStepActivated}
            />
          </div>
        );
      })}
    </div>
  );
}

function AlignedTexts({ steps, children, className }) {
  const { styles } = useMilestoneStepperStyles();
  return (
    <div className={classNames(styles.alignedTexts, className)}>
      {steps.map(step => (
        <div
          key={step.item.key}
          style={{ left: `${step.position}%` }}
          className={styles.position}
        >
          <div
            className={classNames(styles.alignment, {
              secondary: step.item.type === StepType.SECONDARY,
            })}
          >
            {children(step)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MilestoneStepper({
  style,
  dataSource,
  activeStep,
  isLeadingStepActivated = true,
  className,
}) {
  const steps = computePositions(dataSource);
  const { styles } = useMilestoneStepperStyles();

  return (
    <div style={style} className={className}>
      <div className={styles.container}>
        <AlignedTexts
          steps={steps}
          className={classNames('MilestoneStepper-Titles', styles.titles)}
        >
          {step => <span>{step.item.title}</span>}
        </AlignedTexts>
        <AlignedTexts steps={steps} className={styles.subtitles}>
          {step => <span>{step.item.subtitle}</span>}
        </AlignedTexts>
        <div className={styles.timelineSection}>
          <MilestoneTimeline
            steps={steps}
            activeStep={activeStep}
            isLeadingStepActivated={isLeadingStepActivated}
          />
        </div>
        <AlignedTexts
          steps={steps}
          className={classNames(
            'MilestoneStepper-Descriptions',
            styles.descriptions
          )}
        >
          {step => <span>{step.item.description}</span>}
        </AlignedTexts>
      </div>
    </div>
  );
}
